import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Container from "../../components/base/Container.jsx";
import Button from "../../components/base/Button.jsx";
import StepIndicator from "../../components/base/StepIndicator.jsx";
import StickyColumn from "../../components/StickyColumn.jsx";
import LoadingOverlay from "../../components/LoadingOverlay.jsx";
import Step1 from "./step1.jsx";
import Step2 from "./step2.jsx";
import Step3 from "./step3.jsx";
import { theme } from "../../theme.js";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";

const API_URL = process.env.REACT_APP_API_URL;

// Helper function to convert date to YYYY-MM-DD format without timezone issues
const toDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function RaceRegistration() {
  const { event_id, race_id, price_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get transfer_id from query params
  const queryParams = new URLSearchParams(location.search);
  const transfer_id = queryParams.get("transfer_id");
  const isTransferMode = transfer_id ? true : false;

  const [step, setStep] = useState(1); // 1, 2 ou 3
  const isEditMode = location.state?.editMode || false;
  const bibId = location.state?.bib_id; // Get bib_id from edit mode navigation
  const [race, setRace] = useState(null);
  const [price,setPrice] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [options, setOptions] = useState([]);
  const [corrals, setCorrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [organizer, setOrganizer] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState("");
  const [transferInvalidModal, setTransferInvalidModal] = useState(false);
  const [prefillData, setPrefillData] = useState(null); // Store original prefilled data when "myself" is selected
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    // If in edit mode, skip to step 3
    if (isEditMode) {
      setStep(3);
    }
  }, [isEditMode]);

  // Validate transfer status if transfer mode
  useEffect(() => {
    if (isTransferMode && transfer_id) {
      const validateTransfer = async () => {
        try {
          console.log("Validating transfer:", transfer_id);
          const res = await fetch(`${API_URL}/api/transfers/validate/${transfer_id}`);
          const data = await res.json();
          console.log("Transfer validation response:", res.status, data);
          
          // Show invalid modal if:
          // 1. Request failed (not 200)
          // 2. Status is not "Pending"
          if (!res.ok) {
            console.log("Transfer validation failed - showing invalid modal");
            setTransferInvalidModal(true);
          } else if (data.status && data.status !== "Pending") {
            console.log("Transfer status is not Pending:", data.status, "- showing invalid modal");
            setTransferInvalidModal(true);
          } else {
            console.log("Transfer is valid - status: Pending");
          }
        } catch (error) {
          console.error("Error validating transfer:", error);
          setTransferInvalidModal(true);
        }
      };
      
      validateTransfer();
    }
  }, [isTransferMode, transfer_id]);

  const [formData, setFormData] = useState({
    // Étape 1: Qui prendra le départ
    participantType: "self", // "self" ou "other"
    firstname: "",
    lastname: "",
    email: "",
    gender: "",
    birthdate: "",
    phone: "",

    // Étape 2: Adresse
    street_number: "",
    postal_code: "",
    city: "",

    // Contact d'urgence
    emergency_contact: "",
    emergency_phone: "",

    //options
    extraOptions: {},
    corrals: "",
  });

  const getUserData = () => {
    const userData = localStorage.getItem("participant");
    const user = userData ? JSON.parse(userData) : null;
    
    // Always set participantType to "self", even if user data is not found
    const prefilled = user ? {
      firstname: user.firstname || "",
      lastname: user.lastname || user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      gender: user.gender || "",
      birthdate: toDate(user.birthdate) || "",
      emergency_contact: user.emergency_contact || "",
      emergency_phone: user.emergency_phone || "",
      street_number: user.street_number || "",
      postal_code: user.postal_code || "",
      city: user.city || "",
      participant_id: user.id_bdd || "",
    } : {
      firstname: "",
      lastname: "",
      email: "",
      phone: "",
      gender: "",
      birthdate: "",
      emergency_contact: "",
      emergency_phone: "",
      street_number: "",
      postal_code: "",
      city: "",
      participant_id: "",
    };
    
    // Store original prefilled data for change detection
    setPrefillData(prefilled);
    
    setFormData((prev) => ({
      ...prev,
      participantType: "self",
      ...prefilled,
    }));
  };



  const cleanUserData = () => {
    setPrefillData(null); // Clear prefilled data when switching to "other"
    setFormData((prev) => ({
      ...prev,
      participantType: "other",
      firstname: "",
      lastname: "",
      email: "",
      phone: "",
      gender: "",
      birthdate: "",
      phone: "",
      emergency_contact: "",
      emergency_phone: "",
      street_number: "",
      postal_code: "",
      city: "",

    }));
  };

  const steps = [
    { number: 1, label: "Informations personnelles" },
    { number: 2, label: "Informations supplémentaires" },
    { number: 3, label: "Options & documents obligatoires" },
  ];

  useEffect(() => {
    const fetchRaceDetails = async () => {
      try {
        // Récupérer les infos de la course
        const raceRes = await fetch(`${API_URL}/api/races/${race_id}`);
        const raceData = await raceRes.json();
        const currentRace = raceData;
        setRace(currentRace);
        // Récupérer les options disponibles
        setOptions(currentRace.options || []);

        // Récupérer les documents requis
        setRequirements(currentRace.requirements || []);

        setCorrals(currentRace.corrals || []);
        
        // Pré-remplir les infos de l'utilisateur connecté si "Moi-même"
        if (formData.participantType === "self") {
          getUserData();
        } else {
          cleanUserData();
        }
      } catch (error) {
        console.error("Error fetching race details:", error);
      } finally {
        setLoading(false);
      }
      try {
        // Récupérer les infos du prix sélectionné
        const priceRes = await fetch(`${API_URL}/api/prices/${price_id}`);
        const priceData = await priceRes.json();
        console.log("Price data fetched:", priceData);
        setPrice(priceData);
      } catch (error) {
        console.error("Error fetching price details:", error);
      }

      // If in edit mode, fetch existing options and documents
      if (isEditMode && bibId) {
        try {
          console.log("Loading existing options and documents for bib:", bibId);
          const editDetailsRes = await fetch(`${API_URL}/api/registrations/${bibId}/edit-details`);
          if (editDetailsRes.ok) {
            const editDetails = await editDetailsRes.json();
            console.log("Edit details fetched:", editDetails);
            
            // Store existing options and documents (participant data updates are not handled in edit mode)
            setFormData(prev => ({
              ...prev,
              existingOptions: editDetails.existing_options || {},
              existingDocuments: editDetails.existing_documents || {},
              existingCorral: editDetails.existing_corral || null,
            }));
            console.log("Existing options:", editDetails.existing_options);
            console.log("Existing documents:", editDetails.existing_documents);
            console.log("Existing corral:", editDetails.existing_corral);
          }
        } catch (error) {
          console.error("Error fetching edit mode details:", error);
        }
      }
    };

    fetchRaceDetails();
  }, [race_id, price_id, isEditMode, bibId]);

  const formatValue = (key, value) => {
    if (!value && value !== 0) return "--";
    if (key.includes("distance")) {
      if (value < 1000) return `${value.toFixed(1)} km`;
      return `${(value / 1000).toFixed(1)} km`;
    }
    if (key.includes("elevation") || key.includes("altitude")) return `${Math.round(value)} m`;
    if (key.includes("duration")) return `${Math.round(value)} min`;
    return value;
  };

  const groupExtraInfo = () => {
    if (!race?.extra_info) return {};
    const grouped = {};
    const entries = Object.entries(race.extra_info);
    
    const hasUnderscore = entries.some(([key]) => key.includes('_'));
    
    if (!hasUnderscore) {
      grouped['Details'] = entries.map(([key, value]) => ({
        key,
        itemKey: key.charAt(0).toUpperCase() + key.slice(1),
        value,
      }));
    } else {
      entries.forEach(([key, value]) => {
        const parts = key.split('_');
        const groupKey = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
        const itemKey = parts.slice(1).join('_') || key;
        if (!grouped[groupKey]) grouped[groupKey] = [];
        grouped[groupKey].push({ key, itemKey, value });
      });
    }
    return grouped;
  };

  const calculateTotalPrice = useCallback(() => {
    // In transfer mode or edit mode, registration price is not charged
    let total = (isTransferMode || isEditMode) ? 0 : (price?.price || 0);
    Object.entries(formData.extraOptions || {}).forEach(([optionId, selected]) => {
      if (selected) {
        const option = options.find(o => String(o.id_bdd) === String(optionId));
        // Don't add price if option already exists (edit mode)
        const isExisting = isEditMode && formData?.existingOptions && formData.existingOptions[optionId];
        if (option && !isExisting) total += option.price || 0;
      }
    });
    return total.toFixed(2);
  }, [isTransferMode, isEditMode, price, formData, options]);

  const getSelectedOptions = useCallback(() => {
    const selected = Object.entries(formData.extraOptions || {})
      .filter(([, selected]) => selected)
      .map(([optionId]) => options.find(o => String(o.id_bdd) === String(optionId)))
      .filter(Boolean)
      // Filter out existing options in edit mode (they won't be charged)
      .filter(option => {
        const optionId = option.id_bdd || option.id;
        const isExisting = isEditMode && formData?.existingOptions && formData.existingOptions[optionId];
        return !isExisting;
      });
    console.log("Selected options:", selected);
    return selected;
  }, [isEditMode, formData, options]);

  const isPhoneComplete = (phoneValue) => {
    if (!phoneValue || !phoneValue.trim()) return false;
    const parts = phoneValue.split('_');
    return parts.length === 2 && parts[0]?.trim() && parts[1]?.trim();
  };

  const isStep1Valid = useCallback(() => {
    return (
      formData.firstname?.trim() &&
      formData.lastname?.trim() &&
      formData.gender &&
      formData.birthdate &&
      formData.email?.trim() &&
      isPhoneComplete(formData.phone)
    );
  }, [formData]);

  const isStep2Valid = useCallback(() => {
    return (
      formData.street_number?.trim() &&
      formData.postal_code?.trim() &&
      formData.city?.trim() &&
      formData.emergency_contact?.trim() &&
      isPhoneComplete(formData.emergency_phone)
    );
  }, [formData]);

  const isStep3Valid = useCallback(() => {
    // Step 3 is always valid (options and documents are optional)
    return true;
  }, []);

  const isCurrentStepValid = useCallback(() => {
    if (step === 1) return isStep1Valid();
    if (step === 2) return isStep2Valid();
    if (step === 3) return isStep3Valid();
    return false;
  }, [step, isStep1Valid, isStep2Valid, isStep3Valid]);

  const handleNextStep = () => {
    setStep(step + 1);
    window.scrollTo(0, 0);
  };

  const handlePrevStep = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  /**
   * Delete all temp files uploaded during this registration session
   * (called on cancel so orphaned files don't accumulate in uploads_tmp/)
   */
  const deleteTempFiles = async () => {
    const idsToDelete = [];

    // Non-license documents: { [req_id]: { file_id: uuid, ... } }
    if (formData.documents) {
      Object.values(formData.documents).forEach((doc) => {
        if (doc?.file_id) idsToDelete.push(doc.file_id);
      });
    }

    // License documents: { [req_id]: { [docName]: { id: uuid, ... } } }
    if (formData.licenseDocuments) {
      Object.values(formData.licenseDocuments).forEach((licenseGroup) => {
        if (licenseGroup && typeof licenseGroup === "object") {
          Object.values(licenseGroup).forEach((doc) => {
            if (doc?.id) idsToDelete.push(doc.id);
          });
        }
      });
    }

    await Promise.all(
      idsToDelete.map((id) =>
        fetch(`${API_URL}/api/uploads/${encodeURIComponent(id)}`, { method: "DELETE" }).catch(() => {})
      )
    );
  };

  const handleCancel = async () => {
    await deleteTempFiles();
    if (isTransferMode) {
      // In transfer mode, redirect to race page
      navigate(`/events/${event_id}/races/${race_id}`);
    } else if (isEditMode && bibId) {
      navigate(`/tickets/${bibId}`);
    } else {
      navigate(-1);
    }
  };

  const checkDuplicateParticipant = async (firstname, lastname, birthdate) => {
    // Validate required fields are not empty
    if (!firstname?.trim() || !lastname?.trim() || !birthdate?.trim()) {
      console.log("[DEBUG] Skipping duplicate check - missing required fields");
      return null;
    }

    try {
      console.log(`[DEBUG] Checking duplicate for: ${firstname} ${lastname} ${birthdate}`);
      const response = await fetch(
        `${API_URL}/api/participants/check-duplicate?firstname=${encodeURIComponent(firstname)}&lastname=${encodeURIComponent(lastname)}&birthdate=${encodeURIComponent(birthdate)}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      console.log("[DEBUG] Duplicate check response:", data);
      return data.exists ? data : null;
    } catch (error) {
      console.error("Error checking duplicate participant:", error);
      return null;
    }
  };

  // Check if participant data changed from prefilled values (for "myself" selections)
  const hasParticipantDataChanged = () => {
    if (!prefillData || formData.participantType !== "self") {
      return false;
    }

    const fieldsToCheck = [
      'firstname',
      'lastname',
      'email',
      'phone',
      'gender',
      'birthdate',
      'street_number',
      'postal_code',
      'city',
      'emergency_contact',
      'emergency_phone',
    ];

    for (const field of fieldsToCheck) {
      const originalValue = prefillData[field] || "";
      const currentValue = formData[field] || "";
      if (String(originalValue).trim() !== String(currentValue).trim()) {
        console.log(`[DEBUG] Participant data changed: ${field} "${originalValue}" != "${currentValue}"`);
        return true;
      }
    }

    return false;
  };



  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    // Validate that required fields are filled (should be checked by step validation, but double-check)
    if (!formData.firstname?.trim() || !formData.lastname?.trim() || !formData.birthdate?.trim()) {
      alert("Veuillez vérifier que le prénom, nom et date de naissance sont remplis.");
      return;
    }

    // Check for duplicate participant before proceeding (only in new registration mode)
    if (!isEditMode) {
      const participantDataChanged = hasParticipantDataChanged();
      
      console.log(`[DEBUG] handleSubmit - participantType: ${formData.participantType}, prefillData exists: ${!!prefillData}, dataChanged: ${participantDataChanged}`);
      
      const duplicate = await checkDuplicateParticipant(
        formData.firstname,
        formData.lastname,
        formData.birthdate
      );
      if (duplicate) {
        setDuplicateMessage(
          `Un participant avec les mêmes informations est déjà inscrit pour cette course.` +
          `\nVeuillez vérifier vos informations ou utiliser ` +
          `un profil différent pour continuer.`
        );
        setShowDuplicateModal(true);
        return;
      }
    }

    try {
      // Show loading overlay
      setIsLoading(true);

      // In edit mode, prepare filtered options and check for changes
      if (isEditMode) {
        // Filter to only NEW options (exclude existing ones) AND changed existing options
        const newOptions = {};
        const changedExistingOptions = {};
        
        Object.entries(formData.extraOptions || {}).forEach(([optionId, optionData]) => {
          const isExisting = formData?.existingOptions && formData.existingOptions[optionId];
          
          if (!isExisting) {
            // This is a new option
            newOptions[optionId] = optionData;
          } else {
            // Check if existing option value has changed
            const existingValue = formData.existingOptions[optionId]?.value;
            const currentValue = optionData?.value;
            if (existingValue !== currentValue) {
              changedExistingOptions[optionId] = optionData;
              console.log(`Option ${optionId} value changed from ${existingValue} to ${currentValue}`);
            }
          }
        });

        // Check if corral has changed
        const corralChanged = formData.corrals !== (formData?.existingCorral?.name || "");

        // Check if any documents were newly uploaded or changed
        // formData.documents / formData.licenseDocuments contain only freshly uploaded temp files
        const newDocs = formData.documents
          ? Object.values(formData.documents).filter((d) => d?.file_id)
          : [];
        const newLicenseDocs = formData.licenseDocuments
          ? Object.values(formData.licenseDocuments).flatMap((group) =>
              group && typeof group === "object" ? Object.values(group).filter((d) => d?.id) : []
            )
          : [];
        const documentsChanged = newDocs.length > 0 || newLicenseDocs.length > 0 || (formData.deletedDocuments && formData.deletedDocuments.length > 0);

        // Calculate total cost for NEW options only
        let optionsTotal = 0;
        Object.entries(newOptions || {}).forEach(([optionId, optionData]) => {
          if (optionData) {
            const option = options.find(o => String(o.id_bdd) === String(optionId));
            if (option) optionsTotal += option.price || 0;
          }
        });

        // If nothing changed and no new paid options, skip without updating
        if (optionsTotal === 0 && !corralChanged && Object.keys(changedExistingOptions).length === 0 && !documentsChanged) {
          console.log("No changes detected - skipping update");
          setIsLoading(false);
          alert("Aucune modification détectée");
          return;
        }

        // If no new paid options, update directly without payment
        if (optionsTotal === 0) {
          console.log("Edit mode with 0€ cost - updating directly without payment");
          
          try {
            let userId = null;
            const userStr = localStorage.getItem("user");
            if (userStr) {
              try {
                const user = JSON.parse(userStr);
                userId = user?.id || user?.user_id || null;
              } catch (e) {
                console.error("Failed to parse user from localStorage:", e);
              }
            }

            const updatePayload = {
              race_id: race_id,
              bib_id: bibId,
              extraOptions: {...newOptions, ...changedExistingOptions},  // New options + changed existing options
              corrals: formData.corrals,
              ...(formData.documents ? { documents: formData.documents } : {}),
              ...(formData.licenseDocuments ? { licenseDocuments: formData.licenseDocuments } : {}),
              ...(formData.deletedDocuments?.length ? { deletedDocuments: formData.deletedDocuments } : {}),
            };

            const updateRes = await fetch(`${API_URL}/api/registrations`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                ...updatePayload,
                user_id: userId,
                cardName: `${formData.firstname || ""} ${formData.lastname || ""}`.trim(),
                cardEmail: formData.email,
              }),
            });

            if (!updateRes.ok) {
              const error = await updateRes.text();
              throw new Error(`Update failed: ${error}`);
            }

            const updateData = await updateRes.json();
            console.log("Registration updated successfully:", updateData);
            
            // Email is now sent in background - redirect immediately
            alert("Inscription mise à jour avec succès!");
            localStorage.removeItem("registrationData");
            const ticketId = updateData.bib_id || bibId;
            navigate(`/tickets/${ticketId}`);
          } catch (error) {
            console.error("Error updating registration:", error);
            setIsLoading(false);
            alert("Erreur lors de la mise à jour de l'inscription");
          }
          return;
        }

        // If we reach here, there are new paid options - go to payment with only new options
        const registrationPayload = {
          race_id: race_id,
          bib_id: bibId,
          extraOptions: {...newOptions, ...changedExistingOptions},  // New options + changed existing options
          corrals: formData.corrals,
          ...formData.documents ? { documents: formData.documents } : {},
          ...formData.licenseDocuments ? { licenseDocuments: formData.licenseDocuments } : {},
          ...(formData.deletedDocuments?.length ? { deletedDocuments: formData.deletedDocuments } : {}),
          firstname: formData.firstname,
          lastname: formData.lastname,
          email: formData.email,
          phone: formData.phone,
          gender: formData.gender,
          birthdate: formData.birthdate,
          street_number: formData.street_number,
          postal_code: formData.postal_code,
          city: formData.city,
          emergency_contact: formData.emergency_contact,
          emergency_phone: formData.emergency_phone,
        };
        
        const serializable = { ...registrationPayload };
        localStorage.setItem("registrationData", JSON.stringify(serializable));
        setIsLoading(false);
        navigate(`/events/${race_id}/races/${race_id}/${price_id}/payment`, {
          state: { editMode: isEditMode }
        });
        return;
      }

      // Normal registration flow (not edit mode)
      const participantDataChanged = hasParticipantDataChanged();
      const registrationPayload = {
        race_id: race_id,
        price_id: price_id,
        ...formData,
        ...(participantDataChanged && { createNewParticipant: true }),
      };

      // Check if this is a transfer acceptance flow
      if (isTransferMode) {
        // Calculate if there are paid options in transfer mode
        let optionsTotal = 0;
        Object.entries(registrationPayload.extraOptions || {}).forEach(([optionId, selected]) => {
          if (selected) {
            const option = options.find(o => String(o.id_bdd) === String(optionId));
            if (option) optionsTotal += option.price || 0;
          }
        });

        console.log(`[TRANSFER] Transfer mode - optionsTotal: ${optionsTotal}`);

        // If there are paid options, go to payment page
        if (optionsTotal > 0) {
          console.log("[TRANSFER] Paid options detected - redirecting to payment");
          const serializable = { ...registrationPayload };
          localStorage.setItem("registrationData", JSON.stringify(serializable));
          setIsLoading(false);
          navigate(`/events/${race_id}/races/${race_id}/${price_id}/payment`, {
            state: { transferMode: true, transfer_id: transfer_id }
          });
          return;
        }

        // No paid options - submit directly
        try {
          let userId = null;
          const userStr = localStorage.getItem("user");
          if (userStr) {
            try {
              const user = JSON.parse(userStr);
              userId = user?.id || user?.user_id || null;
            } catch (e) {
              console.error("Failed to parse user from localStorage:", e);
            }
          }

          const transferPayload = {
            race_id: race_id,
            ...registrationPayload,
            user_id: userId,
            transfer_id: transfer_id,
            cardName: `${formData.firstname || ""} ${formData.lastname || ""}`.trim(),
            cardEmail: formData.email,
          };

          const res = await fetch(`${API_URL}/api/registrations`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(transferPayload),
          });

          if (!res.ok) {
            const error = await res.text();
            throw new Error(`Transfer acceptance failed: ${error}`);
          }

          const data = await res.json();
          console.log("Transfer accepted successfully:", data);
          localStorage.removeItem("registrationData");
          // Redirect to tickets page
          navigate(`/tickets/${data.public_id}`);
        } catch (error) {
          console.error("Error accepting transfer:", error);
          setIsLoading(false);
          alert("Erreur lors de l'acceptation du transfert");
        }
      } else {
        // Normal flow: Save non-file registration data to localStorage for the payment page
        const serializable = { ...registrationPayload };
        localStorage.setItem("registrationData", JSON.stringify(serializable));
        // Redirect to payment
        setIsLoading(false);
        navigate(`/events/${race_id}/races/${race_id}/${price_id}/payment`, {
          state: { editMode: isEditMode }
        });
      }
    } catch (error) {
      console.error("Error saving registration data:", error);
      setIsLoading(false);
      alert("Erreur lors de la sauvegarde des données");
    }
  };

  if (loading) {
    // Continue to render page with LoadingOverlay shown
  }

  // Shared summary content
  const summaryContent = race ? (
    <>
      <img
        src={race.image || "https://mdidlwnnrgzwjwgydotv.supabase.co/storage/v1/object/public/event/default/pexels-runffwpu-10313674.jpg"}
        alt={race.name}
        style={styles.raceImage}
        onError={(e) => e.target.src = "https://mdidlwnnrgzwjwgydotv.supabase.co/storage/v1/object/public/event/default/pexels-runffwpu-10313674.jpg"}
      />
      <h3 style={styles.raceName}>{race.name}</h3>
      <div style={styles.summaryItem}>
        <span style={styles.summaryLabel}>Date: {new Date(race.date).toLocaleDateString()}</span>
      </div>
      <div style={styles.summaryItem}>
        <span style={styles.summaryLabel}>Type: {race.sport}</span>
      </div>
      {(!isEditMode || getSelectedOptions().length > 0) && (
        <div style={styles.priceSection}>
          <div style={styles.summaryLabel}>Prix</div>
          {!isEditMode && (
            <div style={styles.priceDetail}>
              <span style={styles.priceDetailLabel}>{price?.name || "Inscription"}</span>
              <span style={styles.priceDetailValue}>
                {isTransferMode ? "Gratuit" : `${price?.price || 0} €`}
              </span>
            </div>
          )}
          {getSelectedOptions().map((option) => (
            <div key={option.id_bdd} style={styles.priceDetail}>
              <span style={styles.priceDetailLabel}>{option.name}</span>
              <span style={styles.priceDetailValue}>+{option.price} €</span>
            </div>
          ))}
          <div style={styles.priceTotal}>
            <span style={styles.priceTotalLabel}>Total</span>
            <span style={styles.priceTotalValue}>
              {isTransferMode && parseFloat(calculateTotalPrice()) === 0 
                ? "Gratuit" 
                : `${calculateTotalPrice()} €`
              }
            </span>
          </div>
        </div>
      )}
    </>
  ) : null;

  return (
    <div style={styles.pageContainer}>
      <StepIndicator title="Inscription à la course" steps={steps} currentStep={step} />
      {/* Mobile collapsible summary */}
      {isMobile && race && (
        <div style={styles.mobileSummary}>
          <button
            type="button"
            onClick={() => setSummaryOpen(o => !o)}
            style={styles.mobileSummaryToggle}
          >
            <span style={{ fontWeight: 700 }}>{race.name}</span>
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontWeight: 700, color: theme.colors.primary }}>
                {isTransferMode && parseFloat(calculateTotalPrice()) === 0 
                  ? "Gratuit" 
                  : `${calculateTotalPrice()} €`
                }
              </span>
              <span style={{ transition: "transform 0.2s", display: "inline-block", transform: summaryOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
            </span>
          </button>
          {summaryOpen && (
            <div style={styles.mobileSummaryBody}>
              {summaryContent}
            </div>
          )}
        </div>
      )}
      <Container>
        <div style={{ ...styles.layout, gridTemplateColumns: isMobile ? "1fr" : "1fr 450px" }}>
          {/* Colonne principale */}
            <form onSubmit={(e) => e.preventDefault()} style={{ ...styles.mainColumn }}>
              {step === 1 && (
                <Step1
                  formData={formData}
                  setFormData={setFormData}
                  getUserData={getUserData}
                  cleanUserData={cleanUserData}
                  isReadOnly={isEditMode}
                />
              )}

              {step === 2 && (
                <Step2
                  formData={formData}
                  setFormData={setFormData}
                  isReadOnly={isEditMode}
                />
              )}

              {step === 3 && (
                <Step3
                  formData={formData}
                  setFormData={setFormData}
                  requirements={requirements}
                  options={options}
                  corrals={corrals}
                  isEditMode={isEditMode}
                  isReadOnly={false}
                />
              )}

              <div style={styles.stepActions}>
                {step > 1 && (
                <Button
                  icon={ArrowLeftIcon}
                  title="Précédent"
                  onClick={handlePrevStep}
                />)}
                {step < steps.length && (
                <Button
                      icon={ArrowRightIcon}
                      iconPosition="right"
                      title="Suivant"
                      onClick={handleNextStep}
                      disabled={!isCurrentStepValid()}
                />)}
                {step === steps.length && (
                <Button
                  title={isEditMode ? "Mettre à jour l'inscription" : "Valider l'inscription"}
                  onClick={handleSubmit}
                  disabled={!isCurrentStepValid()}
                />)}
                <Button
                  title="Annuler"
                  onClick={handleCancel}
                  style = {"cancel"}
                />
              </div>
            </form>

            {/* Sticky Right Column — desktop only */}
            {!isMobile && race && (
              <StickyColumn title="Résumé de la course" topOffset="250px">
                {summaryContent}
              </StickyColumn>
            )}
        </div>
      </Container>
      
      {/* Duplicate Participant Modal */}
      {showDuplicateModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Participant déjà inscrit</h2>
            <p style={styles.modalMessage}>{duplicateMessage}</p>
            <button
              onClick={() => setShowDuplicateModal(false)}
              style={styles.modalButton}
            >
              D'accord
            </button>
          </div>
        </div>
      )}

      {/* Transfer Invalid Modal (non-closable) */}
      {transferInvalidModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Transfert invalide</h2>
            <p style={styles.modalMessage}>Le transfert a été annulé. Le lien ne fonctionne plus.</p>
            <button
              onClick={() => navigate("/tickets")}
              style={styles.modalButton}
            >
              Retourner à mes tickets
            </button>
          </div>
        </div>
      )}
      <LoadingOverlay isVisible={isLoading} message="Traitement en cours..." />
    </div>
  );
}

const styles = {
  pageContainer: {
    backgroundColor: theme.colors.bgLight,
    minHeight: "100vh",
    height: "auto",
    width: "100%",
  },
  mobileSummary: {
    maxWidth: "1500px",
    marginTop: theme.spacing?.lg,
    padding: "0 20px 10px",
    backgroundColor: theme.colors.white,
  },
  mobileSummaryToggle: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${theme.spacing?.sm || "8px"} ${theme.spacing?.md || "16px"}`,
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius?.md || "8px",
    cursor: "pointer",
    fontSize: theme.fonts?.size?.md || "14px",
    fontWeight: theme.fonts?.weight?.semibold || 600,
    color: theme.colors.textInverted,
    marginTop: theme.spacing?.md || "16px",
  },
  mobileSummaryBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing?.sm || "8px",
    padding: theme.spacing?.md || "16px",
    border: `1px solid ${theme.colors.border}`,
    borderTop: "none",
    borderRadius: `0 0 ${theme.radius?.md || "8px"} ${theme.radius?.md || "8px"}`,
    backgroundColor: theme.colors.surface || "#fff",
  },
  layout: {
    maxWidth: "1500px",
    margin: "0 auto",
    padding: "10px 20px",
    display: "grid",
    gap: "40px",
    height: "auto",
  },
  mainColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    height: "auto",
    width: "100%",
    minWidth: 0,
  },
  raceImage: {
    width: "100%",
    height: 200,
    objectFit: "cover",
    borderRadius: "12px 12px 0 0",
  },
  raceName: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: theme.colors.textInverted,
  },
  summaryItem: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing?.xs || 4,
  },
  summaryLabel: {
    fontSize: 14,
    color: theme.colors.textInverted,
  },
  priceSection: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    borderTop: "1px solid #e0e0e0",
    paddingTop: 16,    
    gap: 8,
    marginTop: 16,
  },
  priceDetail: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 13,
    padding: "8px 0"
  },
  priceDetailLabel: {
    color: theme.colors.textMuted,
  },
  priceDetailValue: {
    fontWeight: 600,
    color: theme.colors.success,
  },
  priceTotal: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: theme.spacing?.sm || 8,
    marginTop: theme.spacing?.sm || 8,
    borderTop: "2px solid #333",
  },
  priceTotalLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: theme.colors.text,
  },
  priceTotalValue: {
    fontSize: 18,
    fontWeight: 700,
    color: theme.colors.primary || theme.colors.success,
  },
  stepActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    marginTop: "24px",
    paddingTop: "24px",
    borderTop: "none",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "white",
    borderRadius: theme.radius?.lg || "8px",
    padding: theme.spacing?.lg || "24px",
    maxWidth: "500px",
    width: "90%",
    boxShadow: theme.shadows?.lg || "0 4px 12px rgba(0, 0, 0, 0.15)",
    textAlign: "center",
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: theme.colors.textInverted,
    marginTop: 0,
    marginBottom: theme.spacing?.md || "16px",
  },
  modalMessage: {
    fontSize: 14,
    color: theme.colors.textMuted,
    lineHeight: 1.6,
    marginBottom: theme.spacing?.lg || "24px",
    whiteSpace: "pre-line",
  },
  modalButton: {
    backgroundColor: theme.colors.light,
    color: theme.colors.textInverted,
    border: "none",
    padding: `${theme.spacing?.sm || "8px"} ${theme.spacing?.lg || "24px"}`,
    borderRadius: theme.radius?.md || "6px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  },
};
